import ExportedImage from "next-image-export-optimizer";
import { cn, use4K } from "@/lib/utils";
import { Store } from "stores/store";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";
import { inject, observer } from "mobx-react";
import { useEffect, useState } from "react";
import ButtonChat from "@/components/shared/futureChat/buttons/ButtonChat";
import Card from "@/components/shared/futureChat/cards/Card";
import CardBulletPoint from "@/components/shared/futureChat/cards/CardBulletPoint";

type TutorialProps = {
    onCall?: () => void;
    onCancel?: () => void;
    store?: Store;
};

const Tutorial: React.FC<TutorialProps> = ({ onCall, onCancel, store }) => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }

    const { globalLocalized, chatbotLocalized, tutorialLocalized } = store;

    const [windowWidth, setWindowWidth] = useState(0);

    useEffect(() => {
        setWindowWidth(window.innerWidth);

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const getImageSize = () => {
        if (windowWidth < 480) {
            return chatbotLocalized?.keyvisualDark320px;
        } else if (windowWidth < 768) {
            return chatbotLocalized?.keyvisualDark480px;
        } else if (windowWidth < 1024) {
            return chatbotLocalized?.keyvisualDark768px;
        } else if (windowWidth < 1280) {
            return chatbotLocalized?.keyvisualDark1024px;
        } else if (windowWidth < 1920) {
            return chatbotLocalized?.keyvisualDark1280px;
        } else if (windowWidth >= 1920) {
            return chatbotLocalized?.keyvisualDark1920px;
        }
    };

    const background = getImageSize();

    return (
        <div className="Tutorial | flex h-full w-full flex-col items-center">
            {background !== undefined && background !== null && (
                <div className={cn("absolute h-full w-full")}>
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(background)}
                        alt={getAltTextFromGeneralImage(background)}
                        layout="fill"
                        objectFit="cover"
                    />
                </div>
            )}
            {globalLocalized?.logo !== undefined && globalLocalized?.logo !== null && (
                <div
                    className={cn(
                        "relative h-[158px] w-[247px]",
                        "2xs:h-[237px] 2xs:w-[371px]",
                    )}
                >
                    <ExportedImage
                        src={getImageUrlFromGeneralImage(globalLocalized.logo)}
                        alt={getAltTextFromGeneralImage(globalLocalized.logo)}
                        layout="fill"
                        objectFit="contain"
                    />
                </div>
            )}
            <div
                className={cn(
                    "absolute bottom-[46px] flex w-full flex-col gap-[14px] px-[16px]",
                    "2xs:bottom-[68px] 2xs:w-[364px] 2xs:px-0"
                )}
            >
                <Card
                    className={cn(
                        "overflow-y-scroll bg-whiteShade-10",
                        "2xs:max-h-max 2xs:overflow-y-visible 2xs:px-[38px] 2xs:py-[28px]",
                    )}
                >
                    <div
                        className={cn(
                            "font-frutigerBlackItalic text-[22px] font-black uppercase italic leading-[135%] tracking-minus-2 text-shadow-card-shared-text-stele",
                        )}
                    >
                        {tutorialLocalized?.cardTitle}
                    </div>
                    {tutorialLocalized?.bulletPoints.map(
                        (bulletpoint, bulletpointIndex) =>
                            bulletpoint?.icon !== undefined &&
                            bulletpoint?.icon !== null &&
                            bulletpoint.text !== undefined && (
                                <CardBulletPoint
                                    key={bulletpointIndex}
                                    icon={
                                        <ExportedImage
                                            src={getImageUrlFromGeneralImage(bulletpoint?.icon)}
                                            alt={getAltTextFromGeneralImage(bulletpoint?.icon)}
                                            layout="fill"
                                            objectFit="cover"
                                        />
                                    }
                                >
                                    {bulletpoint?.text}
                                </CardBulletPoint>
                            ),
                    )}
                </Card>
                <div className={cn("flex flex-col gap-[4px] px-[18px] text-white", "2xs:gap-[8px]")}>
                    <div
                        className={cn(
                            "font-frutigerBlackItalic text-[14px] font-black italic leading-[135%] tracking-minus-2 uppercase",
                        )}
                    >
                        {tutorialLocalized?.hintTitle}
                    </div>
                    <div
                        className={cn(
                            "font-frutigerBold text-[10px] font-bold leading-[135%] tracking-minus-2",
                        )}
                    >
                        {tutorialLocalized?.hintDescription}
                    </div>
                </div>
                <div className={cn("flex flex-col gap-[8px]", "2xs:gap-[12px]")}>
                    <ButtonChat
                        iconRight={
                            <ExportedImage
                                src={
                                    tutorialLocalized?.buttonOne.icon !== undefined &&
                                        tutorialLocalized?.buttonOne.icon !== null
                                        ? getImageUrlFromGeneralImage(tutorialLocalized.buttonOne.icon)
                                        : "No image provided"
                                }
                                alt={
                                    tutorialLocalized?.buttonOne.icon !== undefined &&
                                        tutorialLocalized?.buttonOne.icon !== null
                                        ? getAltTextFromGeneralImage(tutorialLocalized.buttonOne.icon)
                                        : "No image provided"
                                }
                                layout="fill"
                                objectPosition="center"
                            />
                        }
                        variant="primary"
                        onClick={onCall}
                    >
                        {tutorialLocalized?.buttonOne.text}
                    </ButtonChat>
                    <ButtonChat
                        iconRight={
                            <ExportedImage
                                src={
                                    tutorialLocalized?.buttonTwo.icon !== undefined &&
                                        tutorialLocalized?.buttonTwo.icon !== null
                                        ? getImageUrlFromGeneralImage(tutorialLocalized.buttonTwo.icon)
                                        : "No image provided"
                                }
                                alt={
                                    tutorialLocalized?.buttonTwo.icon !== undefined &&
                                        tutorialLocalized?.buttonTwo.icon !== null
                                        ? getAltTextFromGeneralImage(tutorialLocalized.buttonTwo.icon)
                                        : "No image provided"
                                }
                                layout="fill"
                                objectPosition="center"
                            />
                        }
                        variant="secondary"
                        onClick={onCancel}
                    >
                        {tutorialLocalized?.buttonTwo.text}
                    </ButtonChat>
                </div>
            </div>
        </div>
    );
};

export default inject("store")(observer(Tutorial));
